import {
  type ApplyHazmatOverrideRequest,
  type GetHazmatResponse,
  type HazmatCodeDto,
  type HazmatOverrideEventDto,
  HazmatService,
  MccsRoles,
  type RemoveHazmatOverrideRequest,
} from '@/@types/generated'

/**
 * What a Hazmat override applies to: an application in progress, or the carrier's certificate.
 * An application's override is copied to the certificate when the certificate is issued.
 */
export type HazmatTarget = { applicationId: string } | { carrierId: string }

/** Staff who can apply or remove an override and see its justification and history. */
export const HAZMAT_OVERRIDE_STAFF_ROLES: MccsRoles[] = [
  MccsRoles.CREDENTIALING_ADMIN,
  MccsRoles.CREDENTIALING,
]

/** GetHazmatResponse with its nulls normalized; codes and history are empty for carrier users. */
export type Hazmat = Omit<
  GetHazmatResponse,
  'overrideHazmat' | 'hazmatCodes' | 'overrideHistory'
> & {
  overrideHazmat: boolean | null
  hazmatCodes: HazmatCodeDto[]
  overrideHistory: HazmatOverrideEventDto[]
}

/** A stable key for a target, for hook dependencies. */
export const hazmatTargetKey = (target: HazmatTarget) =>
  'applicationId' in target
    ? `application:${target.applicationId}`
    : `carrier:${target.carrierId}`

/** The label on a carrier's own Hazmat switch. */
export const HAZMAT_SWITCH_LABEL = 'Treat my company as a Hazmat carrier'

export const hazmatLabel = (isHazmat: boolean) =>
  isHazmat ? 'Hazmat' : 'Non-Hazmat'

export async function getHazmat(target: HazmatTarget): Promise<Hazmat> {
  let response: GetHazmatResponse
  if ('applicationId' in target)
    response = await HazmatService.getApplicationHazmat(target.applicationId)
  else response = await HazmatService.getCarrierHazmat(target.carrierId)

  // The API leaves null properties out of its JSON, so they arrive as undefined
  return {
    ...response,
    overrideHazmat: response.overrideHazmat ?? null,
    hazmatCodes: response.hazmatCodes ?? [],
    overrideHistory: response.overrideHistory ?? [],
  }
}

export function applyHazmatOverride(
  target: HazmatTarget,
  request: ApplyHazmatOverrideRequest
): Promise<void> {
  if ('applicationId' in target)
    return HazmatService.applyApplicationHazmatOverride(
      target.applicationId,
      request
    )
  return HazmatService.applyCarrierHazmatOverride(target.carrierId, request)
}

export function removeHazmatOverride(
  target: HazmatTarget,
  request: RemoveHazmatOverrideRequest
): Promise<void> {
  if ('applicationId' in target)
    return HazmatService.removeApplicationHazmatOverride(
      target.applicationId,
      request
    )
  return HazmatService.removeCarrierHazmatOverride(target.carrierId, request)
}

/**
 * Sets the effective status to isHazmat: matching the system determination removes the
 * override, anything else applies one.
 */
export function setEffectiveHazmat(
  target: HazmatTarget,
  hazmat: Pick<Hazmat, 'systemHazmat'>,
  isHazmat: boolean
): Promise<void> {
  if (isHazmat === hazmat.systemHazmat) return removeHazmatOverride(target, {})
  return applyHazmatOverride(target, { isHazmat })
}
